import { useState } from "react";
import { createRoute, useSearch } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { rootRoute } from "./root.tsx";
import { Desk, ProductStatusBadge, Table, Td } from "../components/desk.tsx";
import { ProductEditor } from "../components/product-editor.tsx";
import { Button, Empty, ErrorBlock, Input, Price, Spinner } from "../components/ui.tsx";
import { orgApi } from "../api/org-client.ts";
import { useIdentities, useMe } from "../api/queries.ts";
import { useRequireLogin } from "../lib/require-login.ts";

/**
 * 供应商后台（桌面版）。
 *
 * 供应商在电脑上传资料：几十个 SKU、几百张图、参数表 —— 手机做不了这活。
 */

interface ProductRow {
  id: number;
  title: string;
  status: string;
  minSupplyPrice: number;
  salesCount: number;
  createdAt: string;
}
interface SkuRow {
  id: number;
  specs: Record<string, string>;
  supplyPrice: number;
  stock: number;
  unit: string;
  skuCode: string;
}
interface OrderRow {
  id: number;
  orderNo: string;
  payment: string;
  fulfillment: string;
  payableAmount: number;
  shipName: string;
  shipPhone: string;
  createdAt: string;
  items: { id: number; titleSnapshot: string; quantity: number }[];
}

function Supplier() {
  const search = useSearch({ from: "/supplier" }) as { orgId?: number; tab?: string };
  const { data: me } = useMe();
  /**
   * 🔴 **路由登录守卫** —— 跟 `/admin` 同一条：没登录直接弹去登录页，
   *    登完回到这一页。以前未登录打开这儿看到的是一个空壳后台。
   * ⚠️ hook 必须排在所有 early return 之前。
   */
  useRequireLogin();
  const { data: identities, isPending } = useIdentities(Boolean(me));
  const qc = useQueryClient();

  const suppliers = (identities?.orgs ?? []).filter((o) => o.type === "supplier");
  const orgId = search.orgId ?? suppliers[0]?.id ?? null;
  const tab = search.tab ?? "products";

  const [editing, setEditing] = useState<number | null>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ title: "", categoryId: "", supplyPrice: "", stock: "" });

  const products = useQuery({
    queryKey: ["supplier", orgId, "products"],
    queryFn: () => orgApi.get<{ items: ProductRow[] }>("/merchant/products?limit=50", orgId!),
    enabled: Boolean(orgId) && tab === "products",
  });

  const summary = useQuery({
    queryKey: ["supplier", orgId, "summary"],
    queryFn: () => orgApi.get<Record<string, number>>("/merchant/products/summary", orgId!),
    enabled: Boolean(orgId),
  });

  const detail = useQuery({
    queryKey: ["supplier", orgId, "product", editing],
    queryFn: () =>
      orgApi.get<ProductRow & { skus: SkuRow[] }>(`/merchant/products/${editing}`, orgId!),
    enabled: Boolean(orgId && editing),
  });

  const orders = useQuery({
    queryKey: ["supplier", orgId, "orders"],
    queryFn: () => orgApi.get<{ items: OrderRow[] }>("/merchant/supply-orders?limit=50", orgId!),
    enabled: Boolean(orgId) && tab === "orders",
  });

  const submit = useMutation({
    mutationFn: (id: number) => orgApi.post(`/merchant/products/${id}/submit`, orgId!, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["supplier", orgId] }),
  });

  const adjustStock = useMutation({
    mutationFn: ({ skuId, delta }: { skuId: number; delta: number }) =>
      orgApi.post(`/merchant/skus/${skuId}/stock`, orgId!, { delta }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["supplier", orgId] }),
  });

  const ship = useMutation({
    mutationFn: ({ id, carrier, trackingNo }: { id: number; carrier: string; trackingNo: string }) =>
      orgApi.post(`/merchant/supply-orders/${id}/ship`, orgId!, { carrier, trackingNo }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["supplier", orgId] }),
  });

  if (isPending) return <Spinner />;
  /** ⚠️ 守卫正在跳去登录页，这里只是跳走前那一帧 —— 别再画一个空壳后台。 */
  if (!me) return <Spinner />;
  /**
   * ⚠️ 登录了、但不是任何供应商的成员：**不能再弹去登录页**（他登录状态是好的，
   *    弹过去只会来回打转）。说清楚 + 给一条回商城的路。
   */
  if (suppliers.length === 0) {
    return (
      <Desk title="供应商后台" nav={[]}>
        <Empty
          title="你还不是任何供应商的成员"
          hint="供应商身份由平台开通，请联系平台管理员。"
          action={{ label: "回商城", to: "/" }}
        />
      </Desk>
    );
  }

  const nav = [
    { to: "/supplier", label: "商品管理", search: { orgId, tab: "products" } },
    { to: "/supplier", label: "待发货", search: { orgId, tab: "orders" } },
  ];

  return (
    <Desk
      title={suppliers.find((s) => s.id === orgId)?.name ?? "供应商后台"}
      nav={nav}
      toolbar={
        <>
          {suppliers.length > 1 && (
            <select
              className="h-9 rounded-md border border-neutral-300 px-2 text-sm"
              value={String(orgId)}
              onChange={(e) => {
                location.search = `?orgId=${e.target.value}&tab=${tab}`;
              }}
            >
              {suppliers.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          )}
          {summary.data && (
            <div className="flex gap-3 text-xs text-ink-500">
              <span>待审核 {summary.data.pending ?? 0}</span>
              <span>已上架 {summary.data.active ?? 0}</span>
              <span>已驳回 {summary.data.rejected ?? 0}</span>
            </div>
          )}
        </>
      }
    >
      {tab === "products" && (
        <>
          {/*
            🔴 供应商必须能自己上架 —— 以前这一页只能改库存和发货，
               新入驻的供应商打开后台是一片空白，第一件货都上不了。
          */}
          {creating ? (
            <div className="mb-4">
              <ProductEditor
                orgId={orgId!}
                onDone={() => setCreating(false)}
                onCancel={() => setCreating(false)}
              />
            </div>
          ) : (
            <div className="mb-4">
              <Button onClick={() => setCreating(true)}>+ 新建商品</Button>
            </div>
          )}

          {products.isPending ? (
            <Spinner />
          ) : products.error ? (
            <ErrorBlock error={products.error} />
          ) : products.data.items.length === 0 ? (
            <Empty title="还没有商品" hint="建商品 → 加规格 → 提交审核 → 平台通过后上架" />
          ) : (
            <Table head={["商品", "状态", "最低供货价", "销量", "创建时间", "操作"]}>
              {products.data.items.map((p) => (
                <tr key={p.id} className="hover:bg-neutral-50">
                  <Td>
                    <button
                      className="text-left text-brand-600 hover:underline"
                      onClick={() => setEditing(editing === p.id ? null : p.id)}
                    >
                      {p.title}
                    </button>
                  </Td>
                  <Td><ProductStatusBadge status={p.status} /></Td>
                  <Td className="tabular-nums"><Price cents={p.minSupplyPrice} /></Td>
                  <Td className="tabular-nums">{p.salesCount}</Td>
                  <Td className="text-xs text-ink-500">
                    {new Date(p.createdAt).toLocaleDateString("zh-CN")}
                  </Td>
                  <Td>
                    {/* ⚠️ 只有草稿/驳回/下架能提交 —— 待审核的不给按钮，避免用户反复点 */}
                    {["draft", "rejected", "offline"].includes(p.status) && (
                      <Button size="sm" variant="outline" onClick={() => submit.mutate(p.id)}>
                        提交审核
                      </Button>
                    )}
                  </Td>
                </tr>
              ))}
            </Table>
          )}

          {submit.error && <ErrorBlock error={submit.error} />}

          {editing && detail.data && (
            <section className="mt-6 rounded-lg border border-neutral-200 bg-white p-5">
              <h2 className="mb-3 text-sm font-medium">{detail.data.title} · 规格与库存</h2>
              <Table head={["规格", "货号", "供货价", "库存", "调整库存"]}>
                {detail.data.skus.map((s) => (
                  <tr key={s.id}>
                    <Td>{Object.values(s.specs).join(" · ") || "默认"}</Td>
                    <Td className="font-mono text-xs">{s.skuCode || "—"}</Td>
                    <Td className="tabular-nums"><Price cents={s.supplyPrice} /></Td>
                    <Td className="tabular-nums">
                      {s.stock} {s.unit}
                    </Td>
                    <Td>
                      {/* 🔴 相对增减不是设值 —— 两人同时补货时"设成 100"会互相覆盖 */}
                      <div className="flex gap-1">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => adjustStock.mutate({ skuId: s.id, delta: 10 })}
                        >
                          +10
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => adjustStock.mutate({ skuId: s.id, delta: -10 })}
                        >
                          −10
                        </Button>
                      </div>
                    </Td>
                  </tr>
                ))}
              </Table>
              {adjustStock.error && <ErrorBlock error={adjustStock.error} />}
              <p className="mt-2 text-xs text-ink-500">
                库存用相对增减，不是设值 —— 两个人同时补货时不会互相覆盖
              </p>
            </section>
          )}
        </>
      )}

      {tab === "orders" && (
        <>
          {orders.isPending ? (
            <Spinner />
          ) : orders.error ? (
            <ErrorBlock error={orders.error} />
          ) : orders.data.items.length === 0 ? (
            <Empty title="没有待处理的订单" />
          ) : (
            <Table head={["单号", "商品", "收货人", "金额", "状态", "操作"]}>
              {orders.data.items.map((o) => (
                <tr key={o.id} className="hover:bg-neutral-50">
                  <Td className="font-mono text-xs">{o.orderNo}</Td>
                  <Td className="max-w-[240px]">
                    {o.items.map((i) => (
                      <p key={i.id} className="truncate text-xs">
                        {i.titleSnapshot} ×{i.quantity}
                      </p>
                    ))}
                  </Td>
                  <Td className="text-xs">
                    {o.shipName}
                    <br />
                    <span className="text-ink-500">{o.shipPhone}</span>
                  </Td>
                  <Td className="tabular-nums"><Price cents={o.payableAmount} /></Td>
                  <Td>
                    {o.payment === "paid" && o.fulfillment === "pending" ? (
                      <span className="text-xs text-amber-700">待发货</span>
                    ) : o.fulfillment === "shipped" ? (
                      <span className="text-xs text-emerald-700">已发货</span>
                    ) : (
                      <span className="text-xs text-ink-500">
                        {o.payment === "pending" ? "待付款" : o.fulfillment}
                      </span>
                    )}
                  </Td>
                  <Td>
                    {/* ⚠️ 未付款的不给发货按钮 —— 后端也会拦，但界面上先别诱导 */}
                    {o.payment === "paid" && o.fulfillment === "pending" && (
                      <ShipForm onShip={(carrier, no) => ship.mutate({ id: o.id, carrier, trackingNo: no })} />
                    )}
                  </Td>
                </tr>
              ))}
            </Table>
          )}
          {ship.error && <ErrorBlock error={ship.error} />}
        </>
      )}
    </Desk>
  );
}

function ShipForm({ onShip }: { onShip: (carrier: string, trackingNo: string) => void }) {
  const [carrier, setCarrier] = useState("德邦");
  const [no, setNo] = useState("");
  return (
    <div className="flex gap-1">
      <input
        className="h-9 w-20 rounded-md border border-neutral-300 px-2 text-xs"
        value={carrier}
        onChange={(e) => setCarrier(e.target.value)}
        placeholder="快递"
      />
      <input
        className="h-9 w-28 rounded-md border border-neutral-300 px-2 text-xs"
        value={no}
        onChange={(e) => setNo(e.target.value)}
        placeholder="运单号"
      />
      <Button size="sm" disabled={!no} onClick={() => onShip(carrier, no)}>
        发货
      </Button>
    </div>
  );
}

export const supplierRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/supplier",
  component: Supplier,
  validateSearch: (s: Record<string, unknown>): { orgId?: number; tab?: string } => ({
    orgId: s.orgId ? Number(s.orgId) : undefined,
    tab: typeof s.tab === "string" ? s.tab : undefined,
  }),
});
